/**
 * +mage-specs (S9, docs/design/classes/specs.md "Sim features"): cheat death once per fight.
 *
 * An aura with `flags.cheatDeath` turns the first killing blow its holder takes into a 1-HP save and
 * runs the aura's `onCheatDeath` effects (Cinderborn's Ashrise: a phoenix form). The instance is then
 * spent; it re-arms when the holder leaves combat, or when the aura is applied afresh (Ashrise Pact
 * re-applies it). Self-harm never kills, so it never spends a save.
 */
import type { AuraInstance, SimContext, Unit } from '../contracts';
import { runEffects } from './effects';

/** An unspent cheat-death instance on `u`, or null. */
export function readyCheatDeath(ctx: Pick<SimContext, 'content'>, u: Unit): AuraInstance | null {
  for (const inst of u.auras) {
    if (inst.cheatDeathSpent) continue;
    if (ctx.content.aura(inst.auraId)?.flags?.cheatDeath) return inst;
  }
  return null;
}

/**
 * The HP floor a hit of `remaining` (after absorbs) leaves on `t`: 1 when an unspent cheat-death
 * aura saves it from a killing blow (the instance is returned to be spent), else 0.
 */
export function cheatDeathFloor(ctx: Pick<SimContext, 'content'>, t: Unit, remaining: number): AuraInstance | null {
  if (remaining < t.hp) return null;
  return readyCheatDeath(ctx, t);
}

/** Spend the save and run the aura's onCheatDeath effects (source = target = the holder). */
export function spendCheatDeath(ctx: SimContext, t: Unit, inst: AuraInstance, depth: number): void {
  inst.cheatDeathSpent = true;
  const def = ctx.content.aura(inst.auraId);
  if (def?.onCheatDeath?.length) runEffects(ctx, def.onCheatDeath, { sourceId: t.id, abilityId: def.id, targetId: t.id, point: null, auraIid: inst.iid, depth: depth + 1 });
}

/** Re-arm every spent save (the holder left combat). */
export function rearmCheatDeath(u: Unit): void {
  for (const inst of u.auras) if (inst.cheatDeathSpent) delete inst.cheatDeathSpent;
}
